/**
 * Sentry Integration Service для клиентского портала
 * Отслеживание ошибок и производительности через Sentry
 *
 * Для активации добавьте VITE_SENTRY_DSN в .env файл
 * Бесплатный план: 5,000 ошибок/месяц
 *
 * Регистрация: https://sentry.io/signup/
 */

// Lazy import Sentry для оптимизации бандла (загружается только если DSN настроен)
let Sentry = null

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || ''
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0'
const ENVIRONMENT = import.meta.env.MODE || 'development'
// Рубильник сбора ошибок: включаем временно, выключаем правкой .env без правки кода.
const SENTRY_ENABLED = String(import.meta.env.VITE_SENTRY_ENABLED || '') === 'true'

// Флаг инициализации
let isInitialized = false

/**
 * Инициализирует Sentry SDK
 * Вызывается в main.js при старте приложения
 * @param {Object} app - Vue app instance
 * @param {Object} router - Vue Router instance
 */
export async function initSentry(app, router) {
  // Явный опт-ин через VITE_SENTRY_ENABLED=true (сейчас включён только в
  // client/.env.production). Выключение = убрать флаг из .env и пересобрать —
  // код трогать не надо. В dev по умолчанию выключен: ошибки разработки
  // сожгут бесплатную квоту 5K событий/мес быстрее реальных клиентских.
  if (!SENTRY_ENABLED) {
    console.log('[Sentry] Disabled (VITE_SENTRY_ENABLED != true)')
    return
  }

  // Не инициализируем без DSN
  if (!SENTRY_DSN) {
    console.log('[Sentry] DSN not configured, skipping initialization')
    return
  }

  // Предотвращаем повторную инициализацию
  if (isInitialized) {
    return
  }

  try {
    // Динамический импорт для code splitting
    const SentryModule = await import('@sentry/vue')
    Sentry = SentryModule

    Sentry.init({
      app,
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      release: `globus-client@${APP_VERSION}`,

      // Session Replay отключён: он пишет видеослепок экрана, а на портале это
      // карта с номерами ТС, адресами и маршрутами клиентов. Для задачи «увидеть,
      // какие ошибки ловят клиенты» достаточно стектрейсов и breadcrumbs,
      // а квота replay расходуется отдельно и выгорает за дни.
      integrations: [],

      // Транзакции производительности не нужны и жрут свою квоту — только ошибки.
      tracesSampleRate: 0,

      // Игнорируем некритичные ошибки
      ignoreErrors: [
        // Сетевые ошибки (частые в мобильном PWA)
        'Network request failed',
        'Failed to fetch',
        // То же самое, но словами Safari/iOS: обрыв fetch у него = "Load failed".
        // Без этой строки правило работало только для Chrome/Firefox, а Safari сыпал
        // отвалы векторных тайлов (tiles.cloudgps.online) в 9 отдельных issue.
        'Load failed',
        'NetworkError',
        'AbortError',
        'OfflineError',
        // Ошибки расширений браузера
        'Extension context invalidated',
        // Telegram-браузер: его собственный мост (postEvent) шумит методами, которых
        // нет в in-app WebView. Наш SDK Telegram не подключён — это чужой скрипт.
        'Error invoking postEvent',
        // Расширения, которые повторно объявляют свои переменные в контексте страницы
        "Identifier 'nativeIframe' has already been declared",
        // workbox-window: registration.installing/waiting обнуляется между тиками опроса
        // service worker'а, и он читает scriptURL у null. На работу PWA не влияет.
        /scriptURL/,
        'ResizeObserver loop limit exceeded',
        // Отмененные запросы
        'The operation was aborted',
        // Ошибки карт (тайлы иногда недоступны)
        'Tile load error',
        // Штатный сценарий: access-токен протух, apiClient сам обновит его по cookie
        'token_required',
        // Старый чанк после деплоя — уже лечится авто-reload guard'ом в main.js
        'Failed to fetch dynamically imported module',
        'Importing a module script failed',
        'error loading dynamically imported module',
        // Тот же stale-chunk, но со стороны роутера: чанк страницы исчез после деплоя.
        // main.js это уже ловит через vite:preloadError и перезагружает вкладку сам.
        "Couldn't resolve component",
        // Шум ResizeObserver: браузеры формулируют по-разному
        'ResizeObserver loop completed with undelivered notifications',
      ],

      // Фильтрация по URL
      denyUrls: [
        // Расширения браузеров
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        // Карты (внешние сервисы)
        /tile\.openstreetmap\.org/i,
        /carto\.com/i,
      ],

      // Добавляем контекст пользователя перед отправкой
      beforeSend(event, hint) {
        // Отсев шума сторонних библиотек. ВАЖНО: здесь, в браузере, пути ещё минифицированные
        // (/assets/leaflet-xxx.js) — читаемые leaflet/dist/leaflet-src появляются только после
        // того, как Sentry применит sourcemap у себя. Поэтому опираемся на имя чанка и текст
        // ошибки, а не на исходные пути.
        //   leaflet Canvas._clear — отложенный requestAnimationFrame дорисовывает кадр уже после
        //     снятия рендерера (this._ctx нет). Лежит в отдельном чанке assets/leaflet-*.
        //   leaflet Draggable/panBy className|classList — тот же класс: document-level
        //     mousemove/mouseup и rAF доигрывают жест по контейнеру карты, который уже размонтирован
        //     (this._element === undefined), поэтому addClass/removeClass падают на getClass.
        //   centrifuge — connectTimeout закрывает обнулённый транспорт после разморозки вкладки.
        //     Живёт в общем index-чанке, поэтому ловим по тексту вместе с механизмом обёрнутого
        //     setTimeout.
        // Ни то, ни другое не ломает работу портала, но даёт ~1000 событий в сутки — это вся
        // бесплатная квота за несколько дней. Наши ошибки при этом остаются видимыми.
        //
        // ВНИМАНИЕ: текст ошибки приходит от браузера в его собственной формулировке, поэтому
        // каждое правило обязано покрывать все три:
        //   Chrome  Cannot read properties of null (reading 'close')      — имени свойства-цепочки нет
        //   Firefox this._transport is null
        //   Safari  null is not an object (evaluating 'this._transport.close')
        const ex = event.exception?.values?.[0]
        const exMessage = String(ex?.value || '')
        const exFiles = (ex?.stacktrace?.frames || []).map((f) => f.filename || f.abs_path || '').join(' ')
        //   leaflet Marker/_latLngToNewLayerPoint — слой сняли с карты прямо во время zoom-анимации.
        //     Events.fire идёт по копии списка подписчиков, поэтому уже снятый слой всё равно
        //     получает 'zoomanim' с this._map === null. Чинится только на стороне leaflet; на
        //     картинку не влияет — следующий кадр перерисовывает всё заново.
        //   leaflet DomUtil.getPosition/_animateZoom — маркер сняли с карты прямо во время
        //     зум-анимации: _leaflet_pos у снятого элемента уже нет, _latLng обнулён.
        const leafletNoise = exFiles.includes('/assets/leaflet-')
          && /clearRect|'save'|_ctx|className|classList|_latLng|_leaflet_pos/.test(exMessage)
        const centrifugeNoise = /_transport|reading 'close'/.test(exMessage)
          && /browserapierrors/.test(String(ex?.mechanism?.type || ''))

        // Криптокошельки (MetaMask и прочие EIP-1193) роняют свои unhandledrejection в страницу:
        // отклоняется голый объект {code, message} с JSON-RPC кодом, стека нет вообще —
        // поэтому ни denyUrls, ни ignoreErrors такое не ловят, только сам отклонённый объект.
        // Диапазоны: -32768..-32000 — зарезервированные JSON-RPC, 4001/4100/4900/4901 — EIP-1193.
        // Web3 на портале нет, так что подобное отклонение — гарантированно чужое расширение.
        // Centrifuge тоже отклоняет {code, message}, но с малыми кодами (1, 2, 109) — он под
        // это правило не попадает и остаётся видимым.
        const rejected = hint?.originalException
        const rejectedCode = rejected && typeof rejected === 'object' && !(rejected instanceof Error)
          && typeof rejected.code === 'number' && rejected.message !== undefined
          ? rejected.code
          : null
        const walletNoise = rejectedCode !== null
          && ((rejectedCode >= -32768 && rejectedCode <= -32000) || [4001, 4100, 4900, 4901].includes(rejectedCode))

        if (leafletNoise || centrifugeNoise || walletNoise) return null

        // Добавляем информацию о текущем пользователе
        const user = localStorage.getItem('client_portal_user')
        if (user) {
          try {
            const userData = JSON.parse(user)
            event.user = {
              id: userData.clientId || userData.id,
              username: userData.username,
            }
          } catch {
            // JSON parse error - игнорируем
          }
        }

        // Добавляем кастомные теги
        event.tags = {
          ...event.tags,
          app_type: 'client',
          pwa: 'serviceWorker' in navigator && !!navigator.serviceWorker.controller,
          online: navigator.onLine,
        }

        return event
      },
    })

    isInitialized = true
    console.log('[Sentry] Initialized successfully')
  } catch (error) {
    // Если Sentry SDK не установлен, логируем предупреждение
    console.warn('[Sentry] Failed to initialize:', error.message)
    console.info('[Sentry] To enable error tracking, install: npm install @sentry/vue')
  }
}

/**
 * Вручную отправить ошибку в Sentry
 * @param {Error} error - Объект ошибки
 * @param {Object} context - Дополнительный контекст
 */
export function captureError(error, context = {}) {
  if (!Sentry || !isInitialized) {
    console.error('[Sentry] Not initialized, error logged locally:', error)
    return
  }

  Sentry.withScope((scope) => {
    // Добавляем контекст
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value)
      })
    }
    if (context.extra) {
      scope.setExtras(context.extra)
    }
    if (context.level) {
      scope.setLevel(context.level)
    }

    Sentry.captureException(error)
  })
}

/**
 * Отправить сообщение в Sentry (не ошибку)
 * @param {string} message - Текст сообщения
 * @param {string} level - Уровень: 'info', 'warning', 'error'
 */
export function captureMessage(message, level = 'info') {
  if (!Sentry || !isInitialized) {
    console.log(`[Sentry] ${level}: ${message}`)
    return
  }

  Sentry.captureMessage(message, level)
}

/**
 * Установить информацию о пользователе
 * Вызывается после успешной авторизации
 * @param {Object} user - Данные пользователя
 */
export function setUser(user) {
  if (!Sentry || !isInitialized) return

  Sentry.setUser({
    id: user.clientId || user.id,
    username: user.username,
  })
}

/**
 * Очистить информацию о пользователе
 * Вызывается при выходе
 */
export function clearUser() {
  if (!Sentry || !isInitialized) return

  Sentry.setUser(null)
}

/**
 * Добавить breadcrumb (хлебную крошку)
 * Для отслеживания действий пользователя
 * @param {Object} breadcrumb - Данные breadcrumb
 */
export function addBreadcrumb(breadcrumb) {
  if (!Sentry || !isInitialized) return

  Sentry.addBreadcrumb({
    category: breadcrumb.category || 'action',
    message: breadcrumb.message,
    level: breadcrumb.level || 'info',
    data: breadcrumb.data,
  })
}

/**
 * Проверяет, инициализирован ли Sentry
 * @returns {boolean}
 */
export function isSentryEnabled() {
  return isInitialized
}

/**
 * Получить текущий ID сессии Sentry (для поддержки)
 * @returns {string|null}
 */
export function getSessionId() {
  if (!Sentry || !isInitialized) return null

  try {
    const client = Sentry.getClient()
    const session = client?.getSession?.()
    return session?.id || null
  } catch {
    return null
  }
}
